import React, { FunctionComponent, useMemo, useState, useEffect } from 'react';
import { Grid, Typography, makeStyles } from '@rbx/ui';
import { EGroupSchemaType, EBasicSchemaType } from '@rbx/docsite-utilities/dist/browser';
import StandardMarkdown from '@modules/ui/markdown/components/StandardMarkdown';
import { useTranslation } from '@rbx/intl';
import StandardMarkdownNoMargin from '@modules/ui/markdown/components/StandardMarkdownNoMargin';
import cloneDeep from 'lodash.clonedeep';
import MarkdownSimpleInlineCode from '@modules/ui/markdown/components/MarkdownSimpleInlineCode';
import SchemaTableRow from './SchemaTableRow';
import { useSchemas, TSchema, TSchemaItem } from './SchemasProvider';
import { getListNameByPropName } from '../../utils/schemaOneOfUtils';
import SchemaTableList, { TSchemaItemList } from './SchemaTableList';

const useStyles = makeStyles()(() => ({
  title: {
    marginBottom: 16,
  },
  googleProtobufValueDescription: {
    marginTop: 8,
    display: 'inline-block',
  },
}));

type TSchemaItemData = TSchemaItem & {
  indent: number;
};

type Item = TSchemaItemData | TSchemaItemList;

const isItemList = (item: Item): item is TSchemaItemList => 'listName' in item;

interface SchemaTableProps {
  className?: string;
  showDescription?: boolean;
  showNoneIfTableIsEmpty?: boolean;
  showTitle?: boolean;
  refName: string;
  indent?: number;
  properties?: TSchemaItem[]; // if defined, displays table of just these properties
}

const addSchemaFlatPropertyToItems = (prevIndent: number) => (items: Item[], prop: TSchemaItem) => {
  const { type, refName: refNameRaw, ...otherProps } = prop;
  const refname = refNameRaw;

  items.push({
    ...otherProps,
    type,
    refName: refname,
    indent: prevIndent + 1,
  });
};

const SchemaTable: FunctionComponent<React.PropsWithChildren<SchemaTableProps>> = ({
  refName,
  className,
  showDescription = false,
  showNoneIfTableIsEmpty = false,
  showTitle = false,
  indent: initialIndent = 0,
  properties,
}) => {
  const { schemas } = useSchemas();
  const { translate, translateHTML } = useTranslation();
  const {
    classes: { title: titleStyle, googleProtobufValueDescription },
  } = useStyles();
  const [schema, setSchema] = useState<TSchema>(cloneDeep(schemas[refName]));
  useEffect(() => {
    setSchema(cloneDeep(schemas[refName]));
  }, [refName, schemas]);

  // this is to convert schema object: {properties: []} to an array of SchemaTableRow with Ident
  // if the property has a refName but !refObject, it will show a button which can be expanded to the obj
  // if the property has both refName and refObject, it will the details of refObject with unexpand icon
  // e.g. {
  //   properties: [
  //     { name: name1, type: type1 },
  //     {
  //       name: name2,
  //       type: type2,
  //       refObject: {
  //         properties: [{ name: name3, type: type3 }],
  //       },
  //     },
  //   ],
  // };
  // will convert to an array of [
  //   { name: name1, type: type1, indent: 0 },
  //   { name: name2, type: type2, indent: 0 },
  //   { name: name3, type: type3, indent: 1 },
  // ]
  const items = useMemo(() => {
    const res: Item[] = [];
    let resolvedCurrItemIdx = 0;
    const listIdxByListName: Record<string, number> = {};

    const addToItems = addSchemaFlatPropertyToItems(initialIndent);

    const getSchemaResult = (rawSchema: TSchema, indent: number) => {
      if (!properties && !rawSchema?.properties) return;
      const listNameByPropNameDict = properties ? {} : getListNameByPropName(rawSchema);

      (properties ?? rawSchema.properties).forEach((prop) => {
        // handle lists, whose items are ordered & stored flat onto obj.properties
        const listName = listNameByPropNameDict[prop.name];
        if (listName) {
          const resolvedListIdx = listIdxByListName[listName] ?? resolvedCurrItemIdx;
          res[resolvedListIdx] = res[resolvedListIdx] ?? {
            listName,
            items: [],
            indent,
          };
          (res[resolvedListIdx] as TSchemaItemList).items.push(prop);
          listIdxByListName[listName] = resolvedListIdx;
        } else {
          addToItems(res, prop);
        }
        resolvedCurrItemIdx += 1;
      });
    };
    getSchemaResult(schema, initialIndent);

    return res;
  }, [schema, properties, initialIndent]);

  return (
    <Grid container direction='column' className={className}>
      {showTitle && (
        <Typography variant='h3' id={schema.name} className={titleStyle}>
          {schema.name}
        </Typography>
      )}
      {showDescription && schema.description && items.length > 0 && (
        <StandardMarkdown>{schema.description}</StandardMarkdown>
      )}
      {items.map((item: Item, i: number) => {
        if (isItemList(item)) {
          return (
            <SchemaTableList
              key={item.listName}
              refName={refName}
              header={
                item.listName
                  ? translateHTML('Label.OneOfFollowingNamed', [
                      {
                        opening: 'codeStart',
                        closing: 'codeEnd',
                        // eslint-disable-next-line react/no-unstable-nested-components
                        content: () => (
                          <MarkdownSimpleInlineCode className='inline-code'>
                            {item.listName}
                          </MarkdownSimpleInlineCode>
                        ),
                      },
                    ])
                  : translate('Label.OneOf')
              }
              {...item}
            />
          );
        }
        if (
          (item.groupType === EGroupSchemaType.Enum ||
            (item.groupType === EGroupSchemaType.Array && item.format === 'enum')) &&
          (!item.values || (item.values && item.values.length === 0))
        ) {
          return null;
        }
        if (item.type === EBasicSchemaType.GoogleProtobufValue) {
          return (
            <SchemaTableRow
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              {...item}
              indent={item.indent}
              schemaItem={item}
              bottomDescription={
                <StandardMarkdownNoMargin className={googleProtobufValueDescription}>
                  {translate('Label.GoogleProtobufValueDescription')}
                </StandardMarkdownNoMargin>
              }
            />
          );
        }
        // eslint-disable-next-line react/no-array-index-key
        return <SchemaTableRow key={i} schemaItem={item} indent={item.indent} />;
      })}
      {showNoneIfTableIsEmpty && items.length === 0 && (
        <StandardMarkdown>{translate('Label.None')}</StandardMarkdown>
      )}
    </Grid>
  );
};

export default SchemaTable;
